/**
 * Runtime configuration loader.
 *
 * Fetches `${API_BASE_URL}/config` at app boot to discover deployment-specific
 * values (api base, MCP URL, brand). This is what lets a single SPA build run
 * against any deployment — Shiftagent's, AcmeCorp's, anything — without
 * rebuilding. The endpoint is implemented at
 * packages/agent-server/src/features/config/config.routes.ts.
 *
 * Resolution order for the bootstrap API base URL:
 *   1. import.meta.env.VITE_API_BASE_URL (build-time injected env)
 *   2. window.__SHIFTAGENT_BOOT__?.apiBaseUrl (host-page injected — used by
 *      the embeddable <shift-agent> web component pattern)
 *   3. Falls back to the page origin (assumes agent-server is at the same
 *      origin as the SPA — fine for local dev when Vite proxies)
 */
export interface PlatformConfig {
  object: 'platform_config';
  api_base_url: string;
  mcp_url: string;
  web_app_url: string;
  brand: { name: string };
}

declare global {
  interface Window {
    __SHIFTAGENT_BOOT__?: {
      apiBaseUrl?: string;
      mcpUrl?: string;
      brandName?: string;
      customCellMode?: boolean | string;
    };
  }
}

function resolveBootstrapBaseUrl(): string {
  // Resolution priority (highest to lowest):
  //   1. window.__SHIFTAGENT_BOOT__.apiBaseUrl — runtime-injected by the
  //      client-v2 pod's docker-entrypoint at container startup. This is
  //      THE production path; same image runs anywhere, the chart projects
  //      tenant-specific URLs via env vars.
  //   2. import.meta.env.VITE_API_BASE_URL — build-time bake-in. Used for
  //      one-off dev builds where you want a specific API URL hardcoded
  //      into the bundle (rare in practice now that #1 exists).
  //   3. window.location.origin — fallback. Only correct when the SPA and
  //      the API share a hostname (e.g. local Vite dev via the vite.config
  //      proxy, or a single-hostname production deploy).
  if (typeof window !== 'undefined') {
    const fromBoot = window.__SHIFTAGENT_BOOT__?.apiBaseUrl;
    if (fromBoot) return fromBoot.replace(/\/$/, '');
  }

  // Build-time substitution: Vite ONLY statically replaces direct property
  // accesses on `import.meta.env`. Optional chaining + `as any` blocks the
  // replacement, leaving a runtime lookup that resolves to undefined in
  // production bundles. Use the direct form so `VITE_API_BASE_URL` lands
  // as a string literal in the bundle.
  const fromEnv: string | undefined = import.meta.env.VITE_API_BASE_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}

let cached: PlatformConfig | null = null;
let inflight: Promise<PlatformConfig> | null = null;

export async function loadRuntimeConfig(): Promise<PlatformConfig> {
  if (cached) return cached;
  if (inflight) return inflight;

  const base = resolveBootstrapBaseUrl();
  inflight = fetch(`${base}/config`, { credentials: 'omit' })
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(`Runtime config fetch failed: ${res.status}`);
      }
      const data = (await res.json()) as PlatformConfig;
      cached = data;
      return data;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function getApiBaseUrl(): string {
  if (cached?.api_base_url) return cached.api_base_url.replace(/\/$/, '');
  return resolveBootstrapBaseUrl();
}

export function getCachedConfig(): PlatformConfig | null {
  return cached;
}

// Test/SSR hook — lets tests inject a config without hitting the network.
export function __setCachedConfigForTest(cfg: PlatformConfig | null): void {
  cached = cfg;
}

let customCellModeOverride: boolean | null = null;

/**
 * Custom cell mode: the deployment runs purely as a custom-cell backend
 * (AgentOS-style integrations drive turns via API keys), so the console trims
 * to the operational surface. Source of truth is the boot config written by
 * the container entrypoint (SHIFTAGENT_CUSTOM_CELL_MODE) — accepts a real
 * boolean or the string 'true' defensively.
 */
export function isCustomCellMode(): boolean {
  if (customCellModeOverride !== null) return customCellModeOverride;
  if (typeof window === 'undefined') return false;
  const v = window.__SHIFTAGENT_BOOT__?.customCellMode;
  return v === true || v === 'true';
}

// Test hook — mirrors __setCachedConfigForTest.
export function __setCustomCellModeForTest(value: boolean | null): void {
  customCellModeOverride = value;
}
